import { type FormEvent, type ReactNode, useState } from "react";
import {
  type BookingEvent,
  cancelEventRegistration,
  type EventAdminAction,
  type EventEmailTarget,
  type EventOperationResult,
  type EventRegistration,
  markEventRegistrationPaid,
  markEventRegistrationRefunded,
  markEventRegistrationUnpaid,
  promoteEventWaitlist,
  refundEventRegistration,
  resendEventRegistrationConfirmation,
  restoreEventRegistration,
  updateEventRegistration,
} from "./lib/eventAdminRepository";

type Props = {
  event: BookingEvent;
  registration: EventRegistration;
  actions: EventAdminAction[];
  onChanged: (result: EventOperationResult) => Promise<void>;
};

export function EventRegistrationOperations({ event, registration, actions, onChanged }: Props) {
  const [draft, setDraft] = useState({
    firstName: registration.firstName,
    lastName: registration.lastName,
    email: registration.email,
    phone: registration.phone,
    note: registration.note,
    eventRoleId: registration.eventRoleId,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailTarget, setEmailTarget] = useState<EventEmailTarget>("both");
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const role = event.roles.find((candidate) => candidate.id === registration.eventRoleId);
  const history = actions.filter((action) => action.entityId === registration.id).slice(0, 6);
  const canChangeRole = !["paid", "refunded"].includes(registration.paymentStatus);
  const canResend =
    registration.registrationStatus === "confirmed" &&
    ["paid", "not_required"].includes(registration.paymentStatus);

  async function execute(
    operation: () => Promise<EventOperationResult>,
    confirmation?: string,
  ): Promise<void> {
    if (confirmation && !window.confirm(confirmation)) return;
    setBusy(true);
    setError(null);
    setCheckoutUrl(null);
    try {
      const result = await operation();
      setCheckoutUrl(result.checkoutUrl ?? null);
      await onChanged(result);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Die Aktion ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await execute(() =>
      updateEventRegistration({
        registrationId: registration.id,
        ...draft,
      }),
    );
  }

  return (
    <details className="event-admin-registration">
      <summary>
        <span className="event-admin-registration-person">
          <strong>
            {registration.firstName} {registration.lastName}
          </strong>
          <small>
            {role?.label || "Teilnahmeform unbekannt"} · {registration.reference}
          </small>
        </span>
        <span
          className={`event-admin-status event-admin-status--${registration.registrationStatus}`}
        >
          {registrationStatusLabel(registration.registrationStatus)}
        </span>
        <span className={`event-admin-status event-admin-status--${registration.paymentStatus}`}>
          {paymentStatusLabel(registration.paymentStatus)}
        </span>
      </summary>

      <div className="event-admin-registration-body">
        {registration.registrationStatus === "cancelled" &&
        registration.paymentStatus === "paid" ? (
          <p role="alert">
            Zahlung eingegangen, Anmeldung storniert: Bitte Platzverfügbarkeit und Zahlung
            persönlich klären. Die Anmeldung wurde nicht automatisch wieder aktiviert.
          </p>
        ) : null}
        <dl className="event-admin-registration-details">
          <Detail label="E-Mail">
            <a href={`mailto:${registration.email}`}>{registration.email}</a>
          </Detail>
          <Detail label="Telefon">
            {registration.phone ? (
              <a href={`tel:${registration.phone}`}>{registration.phone}</a>
            ) : (
              "–"
            )}
          </Detail>
          <Detail label="Angemeldet">{formatDateTime(registration.createdAt)}</Detail>
          <Detail label="Betrag">
            {formatPrice(registration.amountCents, registration.currency)}
          </Detail>
          <Detail label="Zahlungswahl">{paymentChoiceLabel(registration.paymentChoice)}</Detail>
          <Detail label="Zahlungsmethode">
            {paymentMethodLabel(registration.paymentMethod, registration.paymentProvider)}
          </Detail>
          <Detail label="Zahlungsfrist">
            {registration.paymentDueAt ? formatDateTime(registration.paymentDueAt) : "–"}
          </Detail>
          <Detail label="Bezahlt">
            {registration.paidAt ? formatDateTime(registration.paidAt) : "–"}
          </Detail>
          <Detail label="Abschlussmail Teilnehmer">
            {emailStatusLabel(registration.confirmationEmailStatus)}
          </Detail>
          <Detail label="Abschlussmail Veranstalter">
            {emailStatusLabel(registration.adminNotificationStatus)}
          </Detail>
          <Detail label="Storniert">
            {registration.cancelledAt ? formatDateTime(registration.cancelledAt) : "–"}
          </Detail>
        </dl>

        {(registration.confirmationEmailLastError || registration.adminNotificationLastError) && (
          <div className="event-admin-inline-warning">
            <strong>E-Mail-Hinweis</strong>
            <span>
              {registration.confirmationEmailLastError || registration.adminNotificationLastError}
            </span>
          </div>
        )}

        {registration.note ? (
          <div className="event-admin-registration-note">
            <strong>Nachricht</strong>
            <p>{registration.note}</p>
          </div>
        ) : null}

        <form className="event-admin-edit-form" onSubmit={save}>
          <div className="event-admin-section-title">
            <div>
              <p className="workspace-kicker">Daten korrigieren</p>
              <h4>Teilnehmerangaben</h4>
            </div>
            <button type="submit" disabled={busy}>
              Speichern
            </button>
          </div>
          <div className="event-admin-edit-grid">
            <label>
              Vorname
              <input
                value={draft.firstName}
                onChange={(event) => setDraft({ ...draft, firstName: event.target.value })}
                required
              />
            </label>
            <label>
              Nachname
              <input
                value={draft.lastName}
                onChange={(event) => setDraft({ ...draft, lastName: event.target.value })}
                required
              />
            </label>
            <label>
              E-Mail
              <input
                type="email"
                value={draft.email}
                onChange={(event) => setDraft({ ...draft, email: event.target.value })}
                required
              />
            </label>
            <label>
              Telefon
              <input
                type="tel"
                value={draft.phone}
                onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
              />
            </label>
            <label>
              Teilnahmeform
              <select
                value={draft.eventRoleId}
                onChange={(event) => setDraft({ ...draft, eventRoleId: event.target.value })}
                disabled={!canChangeRole}
              >
                {event.roles.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.label} · {formatPrice(candidate.priceCents, candidate.currency)}
                  </option>
                ))}
              </select>
              {!canChangeRole ? <small>Bei verbuchter Zahlung gesperrt.</small> : null}
            </label>
            <label className="event-admin-edit-note">
              Nachricht / interne Ergänzung
              <textarea
                rows={3}
                value={draft.note}
                onChange={(event) => setDraft({ ...draft, note: event.target.value })}
              />
            </label>
          </div>
        </form>

        <section className="event-admin-actions" aria-label="Buchungsaktionen">
          <div className="event-admin-section-title">
            <div>
              <p className="workspace-kicker">Organisation</p>
              <h4>Status und Zahlung</h4>
            </div>
          </div>
          <div className="event-admin-action-grid">
            {registration.registrationStatus === "waitlist" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void execute(
                    () => promoteEventWaitlist(registration.id),
                    "Diesen Wartelistenplatz reservieren und die Zahlungsinformation versenden?",
                  )
                }
              >
                Platz freigeben
              </button>
            ) : null}

            {registration.registrationStatus === "cancelled" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void execute(
                    () => restoreEventRegistration(registration.id),
                    "Diese Anmeldung wiederherstellen? Die Kapazität wird vorher geprüft.",
                  )
                }
              >
                Anmeldung wiederherstellen
              </button>
            ) : null}

            {registration.registrationStatus !== "cancelled" &&
            ["unpaid", "pending", "failed"].includes(registration.paymentStatus) ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void execute(
                    () => markEventRegistrationPaid(registration.id),
                    "Zahlung wirklich als vollständig eingegangen verbuchen? Danach werden die Abschlussmails versendet.",
                  )
                }
              >
                Zahlung eingegangen
              </button>
            ) : null}

            {registration.paymentStatus === "paid" && registration.paymentProvider !== "stripe" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void execute(
                    () => markEventRegistrationUnpaid(registration.id),
                    "Den Zahlungsstatus wirklich wieder auf offen setzen?",
                  )
                }
              >
                Zahlung wieder öffnen
              </button>
            ) : null}

            {registration.registrationStatus !== "cancelled" ? (
              <button
                className="event-admin-danger"
                type="button"
                disabled={busy}
                onClick={() =>
                  void execute(
                    () => cancelEventRegistration(registration.id),
                    registration.paymentStatus === "paid"
                      ? "Nur stornieren, ohne die Zahlung zu erstatten? Die Zahlung bleibt als bezahlt gespeichert."
                      : "Diese Anmeldung wirklich stornieren?",
                  )
                }
              >
                {registration.paymentStatus === "paid" ? "Nur stornieren" : "Stornieren"}
              </button>
            ) : null}

            {registration.paymentStatus === "paid" && registration.paymentProvider === "stripe" ? (
              <button
                className="event-admin-danger"
                type="button"
                disabled={busy}
                onClick={() =>
                  void execute(
                    () => refundEventRegistration(registration.id),
                    "Die vollständige Stripe-Zahlung jetzt unwiderruflich erstatten und die Anmeldung stornieren?",
                  )
                }
              >
                Stripe erstatten & stornieren
              </button>
            ) : null}

            {registration.paymentStatus === "paid" && registration.paymentProvider !== "stripe" ? (
              <button
                className="event-admin-danger"
                type="button"
                disabled={busy}
                onClick={() =>
                  void execute(
                    () => markEventRegistrationRefunded(registration.id),
                    "Wurde der Betrag außerhalb der App vollständig erstattet? Danach wird die Anmeldung storniert.",
                  )
                }
              >
                Manuelle Erstattung vermerken
              </button>
            ) : null}
          </div>
        </section>

        <section className="event-admin-actions" aria-label="E-Mail-Aktionen">
          <div className="event-admin-section-title">
            <div>
              <p className="workspace-kicker">Kommunikation</p>
              <h4>Abschlussbestätigung erneut senden</h4>
            </div>
          </div>
          <div className="event-admin-email-action">
            <select
              value={emailTarget}
              onChange={(event) => setEmailTarget(event.target.value as EventEmailTarget)}
              disabled={!canResend || busy}
              aria-label="Empfänger der Bestätigung"
            >
              <option value="both">Teilnehmer und Veranstalter</option>
              <option value="participant">Nur Teilnehmer</option>
              <option value="admin">Nur Veranstalter</option>
            </select>
            <button
              type="button"
              disabled={!canResend || busy}
              onClick={() =>
                void execute(() =>
                  resendEventRegistrationConfirmation(registration.id, emailTarget),
                )
              }
            >
              Erneut senden
            </button>
          </div>
          {!canResend ? (
            <small>
              Erst bei bestätigtem und bezahltem beziehungsweise kostenfreiem Platz möglich.
            </small>
          ) : null}
        </section>

        {checkoutUrl ? (
          <div className="event-admin-checkout-link">
            <strong>Neue Zahlungsseite erstellt</strong>
            <a href={checkoutUrl} target="_blank" rel="noreferrer">
              Stripe-Zahlungsseite öffnen
            </a>
          </div>
        ) : null}

        {error ? (
          <div className="workspace-notice error" role="alert">
            {error}
          </div>
        ) : null}

        {history.length > 0 ? (
          <section className="event-admin-history" aria-label="Bearbeitungsverlauf">
            <p className="workspace-kicker">Bearbeitungsverlauf</p>
            <ol>
              {history.map((action) => (
                <li key={action.id}>
                  <strong>{actionLabel(action.action)}</strong>
                  <span>
                    {formatDateTime(action.createdAt)} · {action.actor}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        ) : null}
      </div>
    </details>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function registrationStatusLabel(status: EventRegistration["registrationStatus"]) {
  if (status === "confirmed") return "Bestätigt";
  if (status === "pending") return "Reserviert";
  if (status === "waitlist") return "Warteliste";
  if (status === "cancelled") return "Storniert";
  return "Offen";
}

function paymentStatusLabel(status: EventRegistration["paymentStatus"]) {
  if (status === "paid") return "Bezahlt";
  if (status === "pending") return "Zahlung läuft";
  if (status === "not_required") return "Keine Zahlung";
  if (status === "refunded") return "Erstattet";
  if (status === "failed") return "Fehlgeschlagen";
  return "Offen";
}

function paymentChoiceLabel(choice: EventRegistration["paymentChoice"]) {
  if (choice === "online") return "Onlinezahlung";
  if (choice === "invoice") return "Rechnung";
  if (choice === "cash") return "Barzahlung vor Ort";
  return "Keine Zahlung";
}

function paymentMethodLabel(method: string, provider: string) {
  const labels: Record<string, string> = {
    card: "Karte",
    klarna: "Klarna",
    paypal: "PayPal",
    sepa_debit: "SEPA-Lastschrift",
    bank_transfer: "Überweisung",
  };
  if (method) return labels[method] ?? method;
  if (provider === "stripe") return "Stripe – noch offen";
  if (provider === "manual") return "Manuell / Überweisung";
  return "–";
}

function emailStatusLabel(status: string) {
  if (status === "sent") return "Versendet";
  if (status === "pending") return "Wird versendet";
  if (status === "failed") return "Fehlgeschlagen";
  return "Noch nicht versendet";
}

function actionLabel(action: string) {
  const labels: Record<string, string> = {
    registration_updated: "Teilnehmerdaten geändert",
    waitlist_promoted: "Wartelistenplatz reserviert",
    registration_restored: "Anmeldung wiederhergestellt",
    payment_marked_paid: "Zahlung als eingegangen verbucht",
    payment_marked_unpaid: "Zahlung wieder geöffnet",
    registration_cancelled: "Anmeldung storniert",
    stripe_refund_created: "Stripe-Erstattung ausgelöst",
    manual_refund_recorded: "Manuelle Erstattung vermerkt",
    confirmation_resent: "Abschlussbestätigung erneut versendet",
  };
  return labels[action] ?? action;
}

function formatDateTime(value: string) {
  if (!value) return "–";
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatPrice(value: number | null, currency: string) {
  if (value === null) return "Preis noch offen";
  if (value === 0) return "Kostenfrei";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
  }).format(value / 100);
}
